import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { AuthSession, AuthUser, LogInInput, SignUpInput } from '@musicville/shared';
import { api, refreshSession, setAccessToken } from './api';

interface AuthValue {
  user: AuthUser | null;
  isLoading: boolean;
  logIn: (input: LogInInput) => Promise<AuthUser>;
  signUp: (input: SignUpInput) => Promise<AuthUser>;
  logOut: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.JSX.Element {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const adopt = useCallback((session: AuthSession): AuthUser => {
    setAccessToken(session.accessToken);
    setUser(session.user);
    return session.user;
  }, []);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const session = (await refreshSession()) as AuthSession | null;

      if (cancelled) return;

      if (session) {
        adopt(session);
      } else {
        setAccessToken(null);
        setUser(null);
      }
      setIsLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [adopt]);

  const value = useMemo<AuthValue>(
    () => ({
      user,
      isLoading,
      logIn: async (input) => adopt(await api.post<AuthSession>('/auth/log-in', input)),
      signUp: async (input) => adopt(await api.post<AuthSession>('/auth/sign-up', input)),
      logOut: async () => {
        try {
          await api.post<void>('/auth/log-out');
        } finally {
          setAccessToken(null);
          setUser(null);
        }
      },
      refreshUser: async () => {
        const fresh = await api.get<AuthUser>('/auth/me');
        setUser(fresh);
      },
    }),
    [user, isLoading, adopt],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside <AuthProvider>');
  return value;
}
